import { LogOut, Menu, X, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { APP_ROUTES } from '../config/routes';
import { NotificationBell } from './notifications/NotificationBell';
import { LoginAnnouncementModal } from './notifications/LoginAnnouncementModal';
import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { PageTransition } from './motion/PageTransition';
import { useAuthContext } from '../hooks/useAuthContext';
import { useCurrentOrg } from '../hooks/useCurrentOrg';
import { useSidebarContext } from '../context/SidebarContext';
import Sidebar from './Sidebar';
import ThemeToggle from './ThemeToggle';
import { AccessibleIconButton } from './common';
import { SkipLink } from './SkipLink';
import { KeyboardShortcutsDialog } from './KeyboardShortcutsDialog';
import { useKeyboardShortcutsDialog } from '../hooks/useKeyboardShortcutsDialog';
import { CommandPalette } from './CommandPalette';
import { useCommandPalette } from '../hooks/useCommandPalette';
import { InviteMemberModal } from './InviteMemberModal';
import { AppFooter } from './AppFooter';

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [inviteModalOpen, setInviteModalOpen] = React.useState(false);
  const { logout, isAuthenticated } = useAuthContext();
  const { currentOrg } = useCurrentOrg();
  const { mode, setMode, isHovering, setIsHovering } = useSidebarContext();
  const location = useLocation();
  const { isOpen: shortcutsOpen, close: closeShortcuts } = useKeyboardShortcutsDialog();
  const { toggle: toggleCommandPalette } = useCommandPalette();

  // Global keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+B (Windows/Linux) or Cmd+B (Mac) - Cycle sidebar modes
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        // Cycle through: expanded -> minimal -> hidden -> expanded
        setMode(
          (() => {
            if (mode === 'expanded') return 'minimal';
            if (mode === 'minimal') return 'hidden';
            return 'expanded';
          })(),
        );
      }

      // Ctrl+K (Windows/Linux) or Cmd+K (Mac) - Open command palette
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        // Don't open if input/textarea is focused
        const { tagName } = document.activeElement || {};
        if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
          return;
        }
        toggleCommandPalette();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode, setMode, toggleCommandPalette]);

  // Hide sidebar on welcome page to control user flow during onboarding
  const shouldHideSidebar = location.pathname === '/welcome';

  return (
    <div className="min-h-screen surface-secondary transition-colors select-none">
      {/* Skip link for keyboard navigation (WCAG 2.4.1) */}
      <SkipLink />

      {/* Sidebar - Self-contained navigation (hidden on welcome page) */}
      {!shouldHideSidebar && (
        <nav
          className={`fixed inset-y-0 left-0 transform ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 transition duration-200 ease-in-out z-50`}
          aria-label="Main navigation"
        >
          <Sidebar />
        </nav>
      )}

      {/* Main content */}
      <div
        className={`${(() => {
          if (shouldHideSidebar) return 'lg:pl-0';
          if (mode === 'hidden') return 'lg:pl-0';
          if (mode === 'minimal') return 'lg:pl-20';
          return 'lg:pl-64';
        })()} min-h-screen flex flex-col transition-all duration-300`}
      >
        <header
          role="banner"
          aria-label="Site header"
          className="bg-white/80 dark:bg-zinc-800/80 backdrop-blur-md shadow-xs dark:shadow-zinc-900 py-2 px-4 flex justify-between items-center transition-colors sticky top-0 z-40 border-b border-default shrink-0"
        >
          <div className="flex items-center gap-4">
            {/* Mobile hamburger — inside header flow so it never overlaps page content */}
            {!shouldHideSidebar && (
              <AccessibleIconButton
                icon={sidebarOpen ? <X size={20} /> : <Menu size={20} />}
                label={sidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-expanded={sidebarOpen}
                data-testid="mobile-menu-toggle"
                className="lg:hidden p-2 -ml-2 rounded-lg
                           text-zinc-700 dark:text-zinc-300
                           hover:bg-zinc-100 dark:hover:bg-zinc-800"
              />
            )}
            {/* Desktop: show sidebar toggle when sidebar is hidden */}
            {!shouldHideSidebar && mode === 'hidden' && !isHovering && (
              <button
                onClick={() => setIsHovering(true)}
                onMouseEnter={() => setIsHovering(true)}
                className="hidden lg:flex p-2 rounded-lg text-zinc-500 dark:text-zinc-400
                           hover:bg-zinc-100 dark:hover:bg-zinc-700
                           transition-colors"
                title="Show sidebar"
                aria-label="Show sidebar"
              >
                <Menu className="h-5 w-5" />
              </button>
            )}
            {currentOrg && (
              <Link
                to={APP_ROUTES.ORGANIZATIONS}
                className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
                title="Manage organizations"
              >
                <Building2 size={16} className="text-zinc-600 dark:text-zinc-400" />
                <span className="max-w-[200px] truncate select-none font-medium">
                  {currentOrg.name}
                </span>
              </Link>
            )}
          </div>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            {isAuthenticated && <NotificationBell />}
            {isAuthenticated && (
              <button
                onClick={() => logout()}
                data-testid="logout-button"
                title="Logout"
                aria-label="Logout"
                className="p-2.5 rounded-lg text-zinc-500 dark:text-zinc-400
                           hover:bg-zinc-100 dark:hover:bg-zinc-700
                           focus:outline-hidden focus:ring-2 focus:ring-zinc-300
                           transition-colors"
              >
                <LogOut className="h-5 w-5" />
              </button>
            )}
          </div>
        </header>
        <main
          id="main-content"
          role="main"
          aria-label="Main content"
          className="p-4 flex-1"
          tabIndex={-1}
        >
          <AnimatePresence mode="wait">
            <PageTransition>
              <Outlet />
            </PageTransition>
          </AnimatePresence>
        </main>
        <AppFooter />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Close sidebar"
          tabIndex={-1}
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Keyboard shortcuts dialog (accessible via ? key) */}
      <KeyboardShortcutsDialog open={shortcutsOpen} onClose={closeShortcuts} />

      {/* Command palette (accessible via Cmd+K / Ctrl+K) */}
      <CommandPalette openInviteModal={() => setInviteModalOpen(true)} />

      {/* Invite member modal (triggered from command palette) */}
      {currentOrg && (
        <InviteMemberModal
          isOpen={inviteModalOpen}
          onClose={() => setInviteModalOpen(false)}
          organizationId={currentOrg.id}
          organizationName={currentOrg.name}
          onSuccess={() => {
            setInviteModalOpen(false);
            // Optionally refresh members list or show success toast
          }}
        />
      )}

      {/* Login announcement modal (shows on login for high-priority notifications) */}
      {isAuthenticated && <LoginAnnouncementModal />}
    </div>
  );
}
